/**
 * Render crop prep output to a blob (PNG/JPG/WebP).
 */

import { normalizeCropRect } from "../../../components/shared/imageToolbar/imageCropModel";
import {
  cropOutputMime,
  getSourceCropRect,
} from "./cropPrepModel";
import {
  CIRCLE_CROP_BG_TYPES,
  clampCircleCropBorderWidth,
  clampCircleCropRotation,
  getCircleCropSourceRect,
  normalizeCircleCropBackgroundType,
  resolveCircleCropBackgroundColor,
  resolveCircleCropBorderColor,
} from "./circleCropPrepModel";

function loadImage(url) {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error("Missing image URL"));
      return;
    }
    const img = new Image();
    if (
      typeof url === "string" &&
      /^https?:\/\//i.test(url) &&
      !url.startsWith("blob:") &&
      !url.startsWith("data:")
    ) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image for export"));
    img.src = url;
  });
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Could not export cropped image"));
      },
      mime,
      quality
    );
  });
}

/**
 * @param {string} imageUrl
 * @param {{ x?: number, y?: number, w?: number, h?: number }} cropRect
 * @param {{ maxEdge?: number, format?: string, quality?: number }} [options]
 * @returns {Promise<Blob>}
 */
export async function exportCroppedImageBlob(
  imageUrl,
  cropRect,
  {
    maxEdge = 4096,
    format = "png",
    quality = 0.92,
    circle = false,
  } = {}
) {
  const img = await loadImage(imageUrl);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) {
    throw new Error("Invalid image dimensions");
  }

  const crop = normalizeCropRect(cropRect);
  let sx = Math.round(crop.x * iw);
  let sy = Math.round(crop.y * ih);
  let sw = Math.max(1, Math.round(crop.w * iw));
  let sh = Math.max(1, Math.round(crop.h * ih));

  if (circle) {
    const side = Math.min(sw, sh);
    sx += Math.round((sw - side) / 2);
    sy += Math.round((sh - side) / 2);
    sw = side;
    sh = side;
  }

  const scale = Math.min(1, maxEdge / Math.max(sw, sh));
  const outW = Math.max(1, Math.round(sw * scale));
  const outH = Math.max(1, Math.round(sh * scale));

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not create export canvas");
  }

  if (circle) {
    ctx.beginPath();
    ctx.arc(outW / 2, outH / 2, outW / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
  }

  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

  const mime = cropOutputMime(format);
  const blobQuality = mime === "image/png" ? undefined : quality;
  return canvasToBlob(canvas, mime, blobQuality);
}

/** @deprecated Use exportCroppedImageBlob */
export async function exportCroppedImageToPngBlob(imageUrl, cropRect, options = {}) {
  return exportCroppedImageBlob(imageUrl, cropRect, {
    ...options,
    format: "png",
  });
}

/**
 * Export using full crop prep settings (zoom / image frame aware).
 * @param {string} imageUrl
 * @param {object} settings
 * @param {{ maxEdge?: number }} [options]
 */
export async function exportCropPrepImageBlob(
  imageUrl,
  settings,
  { maxEdge = 4096 } = {}
) {
  const sourceRect = getSourceCropRect(settings);
  return exportCroppedImageBlob(imageUrl, sourceRect, {
    maxEdge,
    format: settings?.outputFormat,
  });
}

/**
 * Export circle crop prep output (circular mask, background, border, zoom aware).
 */
export async function exportCircleCropPrepImageBlob(
  imageUrl,
  settings,
  { maxEdge = 4096, quality = 0.92 } = {}
) {
  const img = await loadImage(imageUrl);
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  if (!iw || !ih) {
    throw new Error("Invalid image dimensions");
  }

  const sourceRect = normalizeCropRect(getCircleCropSourceRect(settings));
  let sx = Math.round(sourceRect.x * iw);
  let sy = Math.round(sourceRect.y * ih);
  let sw = Math.max(1, Math.round(sourceRect.w * iw));
  let sh = Math.max(1, Math.round(sourceRect.h * ih));
  const side = Math.min(sw, sh);
  sx += Math.round((sw - side) / 2);
  sy += Math.round((sh - side) / 2);
  sw = side;
  sh = side;

  const scale = Math.min(1, maxEdge / side);
  const outSize = Math.max(1, Math.round(side * scale));
  const borderWidth = clampCircleCropBorderWidth(settings?.borderWidth);
  const borderPx = Math.round((outSize * borderWidth) / 100);
  const cx = outSize / 2;
  const cy = outSize / 2;
  const outerR = outSize / 2;
  const innerR = Math.max(1, outerR - borderPx);
  const borderColor = resolveCircleCropBorderColor(settings);
  const bgType = normalizeCircleCropBackgroundType(settings?.backgroundType);
  const format = settings?.outputFormat || "png";
  const mime = cropOutputMime(format);
  const destX = cx - innerR;
  const destY = cy - innerR;
  const destSize = innerR * 2;
  const rotation = clampCircleCropRotation(settings?.rotation);
  const rotationRad = (rotation * Math.PI) / 180;
  const flipH = Boolean(settings?.flipH);
  const flipV = Boolean(settings?.flipV);

  const drawRotatedImage = (targetCtx) => {
    targetCtx.save();
    targetCtx.translate(cx, cy);
    targetCtx.rotate(rotationRad);
    targetCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    targetCtx.drawImage(
      img,
      sx,
      sy,
      sw,
      sh,
      -destSize / 2,
      -destSize / 2,
      destSize,
      destSize
    );
    targetCtx.restore();
  };

  const canvas = document.createElement("canvas");
  canvas.width = outSize;
  canvas.height = outSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Could not create export canvas");
  }

  if (mime === "image/jpeg" && bgType === CIRCLE_CROP_BG_TYPES.TRANSPARENT) {
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outSize, outSize);
  }

  if (borderPx > 0) {
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2, true);
    ctx.fillStyle = borderColor;
    ctx.fill("evenodd");
  }

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
  ctx.clip();

  if (bgType === CIRCLE_CROP_BG_TYPES.BLUR) {
    ctx.filter = "blur(22px) saturate(1.05)";
    ctx.drawImage(
      img,
      sx,
      sy,
      sw,
      sh,
      destX - innerR * 0.15,
      destY - innerR * 0.15,
      destSize * 1.3,
      destSize * 1.3
    );
    ctx.filter = "none";
  } else if (bgType === CIRCLE_CROP_BG_TYPES.MULTICOLOR) {
    if (typeof ctx.createConicGradient === "function") {
      const grad = ctx.createConicGradient(Math.PI * 1.16, cx, cy);
      grad.addColorStop(0, "#e8837a");
      grad.addColorStop(0.2, "#f5d0a8");
      grad.addColorStop(0.4, "#c9e4a0");
      grad.addColorStop(0.6, "#a8d4f0");
      grad.addColorStop(0.8, "#d4a8f0");
      grad.addColorStop(1, "#e8837a");
      ctx.fillStyle = grad;
    } else {
      ctx.fillStyle = "#c9e4a0";
    }
    ctx.fillRect(destX, destY, destSize, destSize);
  } else {
    const solid = resolveCircleCropBackgroundColor(settings);
    if (solid) {
      ctx.fillStyle = solid;
      ctx.fillRect(destX, destY, destSize, destSize);
    }
  }

  drawRotatedImage(ctx);
  ctx.restore();

  const blobQuality = mime === "image/png" ? undefined : quality;
  return canvasToBlob(canvas, mime, blobQuality);
}
